/**
 * A class for form validation with dynamic validators and event handling.
 */
class FormValidator {
  /**
   * Constructor to initialize the form validator.
   *
   * @param {string} selector - The CSS selector for the forms to validate.
   * @param {Object} [options={}] - Options for form validation, including custom handlers.
   */
  constructor(selector, options = {}) {
    this.selector = selector;
    this.options = options;
    this.validators = {}; // Stores custom validation methods.
    this.init();
  }

  /**
   * Initialize validation on all forms matching the selector.
   */
  init() {
    const forms = document.querySelectorAll(this.selector);
    forms.forEach((form) => {
      this.setupValidators(form);
      this.addValidationListeners(form.elements);
      this.addSubmitListener(form);
    });
  }

  /**
   * Set up validation for fields in the form based on custom validators.
   *
   * @param {HTMLFormElement} form - The form element to set up validators for.
   */
  setupValidators(form) {
    Object.keys(this.validators).forEach((validator) => {
      form.querySelectorAll(`[${validator}]`).forEach((field) => {
        this.validators[validator](field);
      });
    });
  }

  /**
   * Add input, focus, and blur event listeners for validation on form fields.
   *
   * @param {HTMLFormControlsCollection} elements - The form elements to attach listeners to.
   */
  addValidationListeners(elements) {
    Array.from(elements).forEach((field) => {
      const target = field.closest('[data-uacaps-validate-class]');
      if (!target) return;

      field.addEventListener('input', (e) => this.updateFieldState(e.target));
      field.addEventListener('focus', () => target.classList.add('focus'));
      field.addEventListener('blur', () => target.classList.remove('focus'));
    });
  }

  /**
   * Add a submit event listener to the form to handle validation and submission.
   *
   * @param {HTMLFormElement} form - The form element to add the submit listener to.
   */
  addSubmitListener(form) {
    form.addEventListener('submit', (event) => {
      if (!form.checkValidity()) {
        event.preventDefault();
        event.stopPropagation();
        this.checkFieldsState(form.elements);
      } else if (this.options.onSubmit) {
        event.preventDefault();
        this.options.onSubmit(event, form);
      }

      form.classList.add('was-validated');
    });
  }

  /**
   * Check and update the state of all fields in the form after submission.
   *
   * @param {HTMLFormControlsCollection} elements - The form elements to validate.
   */
  checkFieldsState(elements) {
    Array.from(elements).forEach((field) => this.updateFieldState(field));
  }

  /**
   * Update the validation state of an individual field.
   *
   * @param {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement} field - The field to update.
   */
  updateFieldState(field) {
    const target = field.closest('[data-uacaps-validate-class]');
    if (!target) return;

    if (field.checkValidity()) {
      target.classList.add('is-valid');
      target.classList.remove('is-invalid');
    } else {
      target.classList.add('is-invalid');
      target.classList.remove('is-valid');
    }
  }

  /**
   * Add a custom validator for form fields.
   *
   * @param {string} name - The name of the validator (attribute name in the form).
   * @param {Function} validatorFunc - The validation function to call for fields with the attribute.
   */
  addValidator(name, validatorFunc) {
    this.validators[name] = validatorFunc;
  }
}

export default FormValidator;
